/**
 * client/game/debughook.ts — `window.__sns`: a READ-ONLY game-state probe for e2e tests and the console.
 *
 * OWNER: integration. It never changes game state: it only copies values out of the app, the menus and
 * the session's ClientWorld, and counts the events presentation code saw (world.frameEvents).
 *
 *   __sns.snapshot()  -> plain JSON: screen, phase, time left, local player, players, scoreboard,
 *                        event counts by name, kill log, end-of-match message, net stats
 */
import type { ServerControl } from '@shared/protocol/control';
import { GameEventType, MatchPhase, PEv, PmType, WeaponId, type GameEvent } from '@shared/types';
import type { ClientSession } from '../net/session';
import type { ClientWorld, MenuSystem } from '../types';
import type { GameDebugHandle } from './app';

const GE_NAME = new Map<number, string>(Object.entries(GameEventType).map(([k, v]) => [v, k]));
const PEV_NAME = new Map<number, string>(Object.entries(PEv).map(([k, v]) => [v as number, k]));
const PHASE_NAME = new Map<number, string>(Object.entries(MatchPhase).map(([k, v]) => [v, k]));
const WEAPON_NAME = new Map<number, string>(Object.entries(WeaponId).map(([k, v]) => [v as number, k]));

interface Sources { app: GameDebugHandle; menus: MenuSystem; session: ClientSession | null }

export interface KillLogEntry { time: number; killer: number; victim: number; weapon: string; mod: number; flags: number }

export class SnsDebugHook {
  private src: (() => Sources) | null = null;
  private readonly counts = { game: {} as Record<string, number>, pred: {} as Record<string, number>, ent: {} as Record<string, number> };
  private kills: KillLogEntry[] = [];
  private recent: GameEvent[] = [];
  private end: ServerControl | null = null;
  private phasesSeen: string[] = [];

  bind(src: () => Sources): void {
    this.src = src;
    (window as unknown as { __sns: unknown }).__sns = { snapshot: () => this.snapshot() };
  }

  reset(): void {
    this.counts.game = {}; this.counts.pred = {}; this.counts.ent = {};
    this.kills = []; this.recent = []; this.end = null; this.phasesSeen = [];
  }

  onEnd(m: ServerControl): void { this.end = structuredClone(m); }

  onFrame(world: ClientWorld): void {
    const ph = PHASE_NAME.get(world.phase) ?? String(world.phase);
    if (this.phasesSeen[this.phasesSeen.length - 1] !== ph) this.phasesSeen.push(ph);
    for (const e of world.frameEvents) {
      if (e.src === 'game') {
        const n = GE_NAME.get(e.ge.type) ?? String(e.ge.type);
        this.counts.game[n] = (this.counts.game[n] ?? 0) + 1;
        if (e.ge.type === GameEventType.ENT_EVENT) continue;
        this.recent.push(e.ge);
        if (this.recent.length > 40) this.recent.shift();
        if (e.ge.type === GameEventType.KILL) {
          this.kills.push({ time: e.ge.time, killer: e.ge.killer, victim: e.ge.victim, weapon: WEAPON_NAME.get(e.ge.weapon) ?? String(e.ge.weapon), mod: e.ge.mod, flags: e.ge.flags });
          if (this.kills.length > 200) this.kills.shift();
        }
      } else {
        const bucket = this.counts[e.src];
        const n = PEV_NAME.get(e.ev) ?? String(e.ev);
        bucket[n] = (bucket[n] ?? 0) + 1;
      }
    }
  }

  snapshot(): unknown {
    const s = this.src?.();
    if (!s) return null;
    const sess = s.session;
    const w = sess?.world;
    const ps = w?.predicted;
    const audio = (window as unknown as { __bo2audio?: { played?: string[] } }).__bo2audio;
    return structuredClone({
      app: s.app.state,
      screen: s.menus.current,
      fps: s.app.fps,
      error: s.app.error,
      session: sess?.state ?? null,
      room: sess?.room ? { code: sess.room.code, phase: sess.room.phase, leader: sess.room.leader, players: sess.room.players.map((p) => ({ clientNum: p.clientNum, name: p.name, isBot: p.isBot })) } : null,
      phase: w ? PHASE_NAME.get(w.phase) ?? String(w.phase) : null,
      phasesSeen: this.phasesSeen,
      timeLeftMs: w?.timeLeftMs ?? 0,
      localClientNum: w?.localClientNum ?? -1,
      me: ps && w ? {
        alive: ps.health > 0 && ps.pm_type !== PmType.DEAD && ps.pm_type !== PmType.DEAD_LINKED, health: ps.health, pmType: ps.pm_type, pmFlags: ps.pm_flags,
        origin: [...ps.origin], view: [...w.view.origin], viewangles: [...w.view.viewangles], velocity: [...ps.velocity],
        groundEntityNum: ps.groundEntityNum, viewHeight: ps.viewHeightCurrent,
        weapon: WEAPON_NAME.get(ps.weapon) ?? String(ps.weapon), weaponId: ps.weapon, weaponState: ps.weaponState, weaponAnim: ps.weaponAnim,
        ammoClip: [...ps.ammoClip], ammoStock: [...ps.ammoStock], fireSeq: ps.fireSeq, spawnCount: ps.spawnCount, adsFrac: ps.adsFrac,
      } : null,
      players: w ? w.players.map((p) => ({ clientNum: p.clientNum, name: p.info.name, alive: p.alive, origin: [...p.origin] })) : [],
      scoreboard: w ? w.scoreboard.map((r) => ({ ...r })) : [],
      counts: this.counts,
      kills: this.kills,
      recent: this.recent,
      end: this.end,
      net: w ? { ping: w.stats.ping, renderLag: w.stats.renderLag, correctionsPerSec: w.stats.correctionsPerSec, corrections: sess?.netDebug.corrections ?? 0, maxPredError: w.stats.maxPredError, serverTick: w.stats.serverTick, transport: w.stats.transport } : null,
      killcam: s.app.killcam ? { active: s.app.killcam.active, final: s.app.killcam.final, stats: { ...s.app.killcam.stats } } : null,
      audioPlayed: audio?.played ? audio.played.length : 0,
      /** Sound aliases the audio engine started (its own log keeps the first 2000). */
      audioAliases: audio?.played ? [...new Set(audio.played)] : [],
    });
  }
}
